import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import moment from 'moment-timezone';

const styles = theme => ({
  root: {
    display: 'flex',
    position: 'absolute',
    width: '100%',
  },
  hour: {
    position: 'absolute',
    top: '-10px',
    left: '-42px',
    color: theme.palette.primary.main,
    ...theme.typography.body1,
  },
  hourWithExtraTime: {
    position: 'absolute',
    top: '-10px',
    left: '-100px',
    color: theme.palette.primary.main,
    ...theme.typography.body1,
  },
  hourContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '5rem',
  },
  divider: {
    position: 'relative',
    borderTop: `2px dashed ${theme.palette.facet.accentGray}`,
    display: 'flex',
    flex: 1,
  },
  nowHour: {
    color: theme.palette.facet.accentRed,
    fontWeight: 'bold',
    fontSize: theme.typography.body1.fontSize,
  },
  nowDivider: {
    position: 'relative',
    display: 'flex',
    flex: 1,
    borderTop: `4px solid ${theme.palette.facet.accentRed}`,
    borderRadius: '0 5px 5px 0',
  },
});

const ScheduleDivider = ({
  className,
  classes,
  time,
  windowStart,
  windowEnd,
  displayNowLine,
  additionalTz,
}) => {
  const offset30 = 1000 * 60 * 30; // / 12 * (hourEnd - hourStart); // 30 minute offset for alignment purposes
  const offset60 = 1000 * 60 * 60; // / 12 * (hourEnd - hourStart); // 60 minute offset for alignment purposes

  const formatTime = (t) => {
    if (displayNowLine) {
      return t.format('h:mm');
    }

    return `${t.format('h')}:00`;
  };

  const displayTime = () => {
    if (additionalTz) {
      const additionalTime = moment(time).tz(additionalTz);

      return (
        <div className={classes.hourContainer}>
          <span>{formatTime(additionalTime)}</span>
          <span>{formatTime(time)}</span>
        </div>
      );
    }

    return (
      <div className={classes.hourContainer}>
        <span>{formatTime(time)}</span>
      </div>
    );
  };

  return (
    <div
      className={classNames(classes.root, className)}
      style={{
        top: `${((time.valueOf() - windowStart.valueOf() + offset30)
          / (windowEnd.valueOf() - windowStart.valueOf() + offset60))
        * 100}%`,
      }}
    >
      <div className={classNames(classes.divider, {
        [classes.nowDivider]: displayNowLine,
      })}
      >
        <div className={classNames(classes.hour, {
          [classes.nowHour]: displayNowLine,
          [classes.hour]: additionalTz === null,
          [classes.hourWithExtraTime]: additionalTz,
        })}
        >
          {displayTime()}
        </div>
      </div>
    </div>
  );
};

ScheduleDivider.defaultProps = {
  className: '',
  displayNowLine: false,
  additionalTz: null,
};

ScheduleDivider.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  time: PropTypes.object.isRequired,
  windowStart: PropTypes.object.isRequired,
  windowEnd: PropTypes.object.isRequired,
  displayNowLine: PropTypes.bool,
  /**
   * If specified, this will display an extra time in this tz to the left of the default time displayed.
   */
  additionalTz: PropTypes.string,
};

export default withStyles(styles)(ScheduleDivider);
